<template>
  <div id="printerque-app" :class="{ 'hat-testkennzeichen': testkennzeichenSichtbar }">
    <!-- Das Kennzeichen des Testsystems (T-M32.7, REQ-OPS-061). Es gehoert in
         den RAHMEN und nicht in eine Ansicht: es beschreibt das ganze System,
         nicht die geoeffnete Seite. Die drei Bildschirm-Ansichten sind
         ausdruecklich NICHT im Umfang von T-M32.7 — sie laufen im Vollbild auf
         Kuechen- und Kundenmonitoren und bekommen keinen Streifen. -->
    <TestumgebungBanner v-if="!istBildschirmAnsicht" />

    <!-- Kitchen TV + Abholscreen + Fastline: vollbild, kein Sidebar, kein Auth erforderlich -->
    <template v-if="istBildschirmAnsicht">
      <RouterView />
    </template>

    <!-- Der Start ist gescheitert (REQ-OPS-052 (d)): statt des ewigen
         Ladebalkens eine Fehleransicht mit dem Code und GENAU ZWEI Handlungen.
         Sie steht VOR dem Ladezweig, damit ein Fehlerzustand nie vom Ladebild
         verdeckt wird — und NACH den drei Bildschirm-Ansichten, die den
         Anmelde-Start nicht brauchen. -->
    <template v-else-if="authStore.bootstrapError">
      <div class="app-bootstrap-error">
        <img src="/payfix-logo.png" alt="Payfix" style="height:44px; object-fit:contain;" />
        <h1 class="bootstrap-error-title">{{ t('bootstrap.errorTitle') }}</h1>
        <p class="bootstrap-error-hint">{{ bootstrapHinweis }}</p>
        <p class="bootstrap-error-code">
          {{ t('bootstrap.errorCode', { code: authstoreFehlercode }) }}
        </p>
        <div class="bootstrap-error-actions">
          <button type="button" class="bootstrap-btn bootstrap-btn-primary" @click="startNeuVersuchen">
            {{ t('bootstrap.retry') }}
          </button>
          <button type="button" class="bootstrap-btn" @click="neuAnmelden">
            {{ t('bootstrap.reLogin') }}
          </button>
        </div>
      </div>
    </template>

    <template v-else-if="authStore.isLoading">
      <div class="app-loading">
        <img src="/payfix-logo.png" alt="Payfix" style="height:44px; object-fit:contain;" />
        <div class="loading-bar"><div class="loading-bar-fill"></div></div>
      </div>
    </template>

    <template v-else-if="authStore.isAuthenticated">
      <AppSidebar />
      <main class="app-main">
        <!-- Der sichtbare Verbindungszustand (T-M21.5, REQ-OPS-054): er gehoert
             in den RAHMEN und nicht in eine einzelne Ansicht — betroffen sind
             die Abos der Sitzung, nicht die geoeffnete Seite. Er steht VOR der
             Ansicht, damit er nicht unter ihr scrollt, und ausserhalb des
             Seitenuebergangs, damit er beim Seitenwechsel nicht mit aus- und
             wieder einblendet. Ohne Fehlzustand rendert er nichts. -->
        <LiveVerbindungHinweis />
        <RouterView v-slot="{ Component }">
          <Transition name="page" mode="out-in">
            <component :is="Component" :key="$route.path" />
          </Transition>
        </RouterView>
      </main>
    </template>

    <template v-else>
      <div class="full-page-wrapper">
        <RouterView />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n }          from 'vue-i18n';
import { useAuthStore, PROFIL_FEHLT_CODE } from '@/stores/authStore';
import { useCompanyStore }  from '@/stores/companyStore';
import { usePrintJobStore } from '@/stores/printJobStore';
import { usePrinterStore }  from '@/stores/printerStore';
import { usePrintserverStore } from '@/stores/printserverStore';
import { useLocationStore }    from '@/stores/locationStore';
import { useUserStore }        from '@/stores/userStore';
import { useRegisterStore }    from '@/stores/registerStore';
import { useInvitationStore }  from '@/stores/invitationStore';
import { useApiTokenStore }    from '@/stores/apiTokenStore';
import AppSidebar            from '@/components/AppSidebar.vue';
import LiveVerbindungHinweis  from '@/components/LiveVerbindungHinweis.vue';
import TestumgebungBanner     from '@/components/TestumgebungBanner.vue';
import { istTestumgebung, titelKennzeichenAnwenden } from '@/utils/umgebung';
import { istPortalPfad } from '@/utils/bildschirmSitzung';

const route        = useRoute();
const router       = useRouter();
const { t }        = useI18n();
const authStore    = useAuthStore();
const companyStore = useCompanyStore();
const printJobStore = usePrintJobStore();
const printerStore  = usePrinterStore();
// Die sechs folgenden Stores stehen hier allein fuer die Liste der Sitzungs-Abos
// weiter unten (T-M48.6). Ihr Aufbau hat keine Nebenwirkung — reine `ref`s, kein
// Abo, kein Beobachter —, und `LiveVerbindungHinweis` im Rahmen baut sie ohnehin.
const printserverStore = usePrintserverStore();
const locationStore    = useLocationStore();
const userStore        = useUserStore();
const registerStore    = useRegisterStore();
const invitationStore  = useInvitationStore();
const apiTokenStore    = useApiTokenStore();

// ── Das Kennzeichen des Testsystems (T-M32.7, REQ-OPS-061) ──────────────────

/**
 * Die drei Bildschirm-Kanaele laufen im Vollbild ohne Rahmen — die Bedingung
 * stand bis zum 2026-09-04 dreifach in der Vorlage und ist hier benannt
 * worden, damit das Kennzeichen sie NICHT ein zweites Mal ausschreiben muss.
 * Der Wert ist zeichengleich derselbe wie zuvor.
 */
const istBildschirmAnsicht = computed(
  () => route.name === 'kitchen' || route.name === 'abholscreen' || route.name === 'fastline',
);

/**
 * Der Streifen verschiebt den Rahmen (Seitenleiste, Hauptbereich) um seine
 * Hoehe nach unten — deshalb braucht die Wurzel die Klasse. Die Bildungsregel
 * selbst liegt in `utils/umgebung.ts`.
 */
const testkennzeichenSichtbar = computed(
  () => !istBildschirmAnsicht.value && istTestumgebung(),
);

/**
 * Das Titel-Praefix `[TEST]`. Es steht am DOKUMENT und nicht an einer Ansicht:
 * wer mehrere Tabs offen hat, unterscheidet sie in der Tab-Leiste, bevor er
 * eine Ansicht ueberhaupt sieht. Einmal beim Aufbau gesetzt und idempotent.
 */
titelKennzeichenAnwenden();

// ── Die Fehleransicht des Anmelde-Starts (T-M21.2, REQ-OPS-052 (d)) ──────────

/**
 * Der Fehlercode, wie er in der Ansicht steht. Angezeigt wird der CODE und
 * nicht der Meldungstext: der Code ist das, wonach im Feld gesucht wird
 * (REQ-OPS-052 (c)), der Meldungstext kommt von Firebase auf Englisch und
 * gehoert damit in die Konsole und nicht in eine deutsche Oberflaeche
 * (REQ-OV-002/005).
 */
const authstoreFehlercode = computed(() => authStore.bootstrapError?.code ?? '');

/**
 * Der Hinweistext der Fehleransicht — mit EINEM benannten Sonderfall
 * (T-M30.7, REQ-SEC-029 (b), REQ-DM-070 (e)).
 *
 * Fehlt das Benutzerprofil, hilft weder „Erneut versuchen" noch „Neu anmelden":
 * das Profil entsteht ausschliesslich ueber eine Einladung (REQ-SEC-030), und
 * **die Oberflaeche legt es seit T-M30.7 nicht mehr selbst an**. Der Hinweis
 * nennt deshalb den Weg, der hilft — den Administrator —, statt zu einer
 * Handlung aufzufordern, die zwangslaeufig wieder hier endet. *Die zwei
 * Schaltflaechen bleiben unveraendert stehen (REQ-OPS-052 (d)): sie sind der
 * Weg aus jedem ANDEREN Fehlerzustand, und ein Bildschirm ohne Ausgang waere
 * schlimmer als ein Hinweis, der einmal nicht passt.*
 */
const bootstrapHinweis = computed(() =>
  authstoreFehlercode.value === PROFIL_FEHLT_CODE
    ? t('bootstrap.errorHintNoProfile')
    : t('bootstrap.errorHint'),
);

/** „Erneut versuchen": den Start wiederholen — OHNE Neuladen (REQ-OPS-054 (d)). */
function startNeuVersuchen(): void {
  authStore.retryBootstrap();
}

/** „Neu anmelden": abmelden und zur Anmeldung. */
async function neuAnmelden(): Promise<void> {
  authStore.clearBootstrapError();
  try {
    await authStore.logout();
  } catch {
    // Die Abmeldung darf den Weg zur Anmeldung nicht versperren: scheitert sie
    // (Netz), bleibt der Nutzer sonst in der Fehleransicht gefangen — genau der
    // Zustand, den dieser Bildschirm beseitigt.
  }
  await router.push({ name: 'login' });
}

// ── Die Firmenliste: abonniert, sobald der Anmeldestart FERTIG ist ──────────
//
// ⚠ **BIS ZUM 2026-09-08 STAND HIER ALLEIN `authStore.isAuthenticated`**
// („Subscribe company list as soon as user is authenticated"). *Berichtigt
// statt geloescht — REQ-OPS-036 Praez. 6.* Das war zu frueh, und zwar messbar:
// `verarbeiteAnmeldezustand` setzt `firebaseUser` GANZ ZU BEGINN (Schritt
// `auth.signedIn`), liest den Anspruch aber erst danach (`start.claimRead`).
// Dazwischen lief dieser Watcher, `companyStore.subscribe()` rief
// `pruefeMandantenwahl()`, und die STRENGE Richtung von `darfMandant` — ohne
// gelesenen Anspruch `false` (REQ-SEC-028 (a)) — nahm die gespeicherte Wahl
// samt `pf_company_id` zurueck. Der Waechter sah danach keine Kennung mehr
// (`companyChoice.keine-kennung`), und der Admin landete nach JEDEM Neuladen
// auf `/select-company` — die eigentliche Ursache der Auftraggeber-Meldung aus
// REQ-OPS-058 Praez. 2, unabhaengig vom Rennen, das T-M34.4 behoben hat.
// Im Feld gemessen auf `printerque-test` am 2026-09-08 (T-M34.7): der Eintrag
// fiel bei t=378 ms, `start.claimRead` kam bei t=618 ms.
//
// **DIE REGEL BLEIBT STRENG, ES AENDERT SICH DER ZEITPUNKT** (REQ-SEC-028 (a),
// REQ-OPS-058 Praez. 2 (e)): `pruefeMandantenwahl` ist unveraendert und nimmt
// eine Kennung ausserhalb des Anspruchs weiterhin zurueck — nur eben, nachdem
// der Anspruch gelesen ist. `isLoading` faellt genau dann (`beende()` in
// `authStore`), also ist es dieselbe Bauform wie beim Auftrags-Abo darunter.
//
// ⚠ **NACHGEBESSERT AM 2026-09-08 (T-M34.10, Nachbesserung zu T-M34.8 — Feldbefund 4 der
// Feldprobe T-M34.7, tab-uebergreifend auf `printerque-test` gemessen).**
// *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.* **BIS DAHIN STAND HIER
// `if (authed && !loading)` OHNE ORTSBEZUG.** Das genuegte am Portal und nicht
// im SELBEN Browser daneben: `localStorage` gehoert dem URSPRUNG und nicht dem
// Tab. Wird bei angemeldetem Admin eine Bildschirm-Adresse geoeffnet
// (`/abholscreen/…`, `/kitchen/…`), laeuft `authStore.init()` dort auf der
// Portal-Instanz `[DEFAULT]` und sieht die ADMIN-Sitzung — der Anspruch wird
// dort aber ABSICHTLICH nicht gelesen (`amPortal === false`, kein
// `start.claimRead`), waehrend `isLoading` trotzdem faellt. Der Watcher startete
// daraufhin das Firmen-Abo, `pruefeMandantenwahl` fand keinen Anspruch, und
// `clearCompany()` nahm `pf_company_id` fort — **dem PORTAL-TAB, der sie
// danach beim Neuladen vermisste.** „Start beendet" ist an einer
// Bildschirm-Adresse eben NICHT „Anspruch gelesen" (REQ-OPS-058 Praez. 2 (g2)).
//
// **DAS ABO LAEUFT DESHALB NUR AN PORTAL-PFADEN.** Die Erkennung kommt aus
// `utils/bildschirmSitzung.ts` — **derselben EINEN Quelle wie im `authStore`**
// (`istPortalPfad(location.pathname)`, ueber `utils/legacySw.ts` auf die drei
// Geltungsbereiche aus `utils/kitchenPwa.ts`); eine zweite Pfadliste hier waere
// die Doppelfuehrung, die eine vierte Bildschirm-Ansicht spaeter still
// auslaesst. **An einer Bildschirm-Adresse braucht die Firmenliste ohnehin
// niemand:** Kitchen TV, Abholscreen, Fastline und Light lesen ihren Kanal ueber
// die Anzeige-Sitzung und keine Mandantenliste — an ihrem Ablauf ist damit kein
// Zeichen anders.
//
// **DIE 14 UEBRIGEN AUFRUFER VON `subscribe()` BRAUCHEN KEINE WACHE:** dreizehn
// stehen in `onMounted` einer Ansicht, einer im Neuversuch von
// `components/LiveVerbindungHinweis.vue` — und beides entsteht erst,
// nachdem der Router-Waechter `warteAufAnmeldestart` abgewartet hat. *(Bis zum
// Ausgangstor T-M34.8 stand hier „17 … alle in `onMounted`": die rohe
// grep-Zahl samt Kommentarzeilen; berichtigt statt geloescht.)* Eine Wache
// IM Store waere deshalb eine zweite Fuehrung derselben Bedingung — und eine,
// die ein Abo still verschluckt statt es zu verschieben.

/**
 * Laeuft diese Instanz an einer Portal-Adresse (T-M34.10, 2026-09-08)?
 *
 * **Gelesen wird `location.pathname` und NICHT der Router-Zustand** — dieselbe
 * Begruendung und dieselbe EINE Quelle wie im `authStore`: die Frage faellt beim
 * Aufbau, und ein Ortswechsel innerhalb der Anwendung fuehrt nie aus dem Portal
 * in eine Bildschirm-Ansicht (die vier Adressen werden am Geraet direkt
 * geoeffnet). Der Aufruf steht IM Watcher und nicht als einmalig berechneter
 * Wert daneben, damit er bei jedem Anmeldezustand erneut gestellt wird.
 *
 * *(Bis zum 2026-09-19 stand hier „die drei Adressen" — seit T-M25.1 gibt es
 * mit `/kitchen/:printerId/light` eine vierte; berichtigt statt geloescht,
 * REQ-OPS-036 Praez. 6. An der EINEN Quelle aendert das nichts: die vierte
 * Adresse liegt im Geltungsbereich `/kitchen/` und ist dort mitgedeckt.)*
 *
 * **ZWEI AUFRUFER, EINE QUELLE** (T-M40.11, REQ-OPS-038): der Firmen-Watcher
 * und der Auftrags-Watcher darunter stellen dieselbe Frage an dieselbe
 * Funktion. Eine zweite Pfadwache oder eine abgeschriebene Adressliste waere
 * die Doppelfuehrung, die eine fuenfte Ansicht spaeter still auslaesst.
 */
function laeuftAmPortal(): boolean {
  return istPortalPfad(typeof location === 'undefined' ? '' : (location.pathname ?? ''));
}

// ── DAS ABO ENDET MIT DER SITZUNG (T-M48.6, REQ-OPS-053 Praez. 1) ──────────
//
// **Bis zum 2026-10-08 stand im Watcher darunter allein
// `if (!authed) printerStore.unsubscribe();`** — beendet wurden beim Abmelden
// das Firmen-, das Drucker- und das Auftrags-Abo, nicht aber die Abos, die
// Ansichten ohne Abraeumen setzen (gemessen am `a908a7a`: `printserverStore` in
// sechs, `locationStore` in drei, `userStore` in zwei Ansichten). Sie liefen
// nach der Abmeldung weiter, bis Firestore sie abwies; beendet hat sie allein
// der stille Fall REQ-OPS-053 (c). *Berichtigt statt geloescht, REQ-OPS-036
// Praez. 6.* **(c) bleibt unveraendert das Netz** (Abmeldung in einem anderen
// Tab, jede Luecke dieser Liste) — es ist nur nicht mehr der Weg.
//
// **EINE STELLE, EINE LISTE (Praez. 1 (c)).** Jeder Portal-Store mit eigenem
// Firestore-Abo steht hier mit genau einem Eintrag. Der Waechter
// `test/sitzungsAboEnde.test.ts` erhebt die Stores unter `src/stores/`, die ein
// Abo bilden (`onSnapshot`, `liveAbo`, `subscribeTo…`), und haelt, dass jeder
// hier steht oder dort mit Grund ausgenommen ist — **ein neuer Store ohne
// Eintrag faellt rot.** Der Schluessel `store` ist der Dateiname ohne Endung.
//
// **DIE ANSICHTEN BLEIBEN, WIE SIE SIND:** `JobsView` behaelt ihre Abos beim
// Verlassen bewusst (T-M17.6/T-M18.6); beendet wird hier, an der Sitzung, und
// nicht beim Verlassen einer Seite.
//
// **DIE BILDSCHIRM-ANSICHTEN SIND NICHT BERUEHRT (Praez. 1 (e), REQ-SC-109):**
// sie halten keines dieser Abos, sondern ihr eigenes ueber `useScreenChannel`
// auf der Instanz `screen`. Die Liste nennt keinen ihrer Wege.
//
// **FIRMENWECHSEL (Praez. 1 (b)) — gemessen, nicht angenommen.** Der Wechsel
// fuehrt ueber `/select-company` (Seitenleiste → `CompanySelectView`:
// `selectCompany(id)`, dann `/jobs`); dabei wird die vorige Ansicht abgebaut
// (`:key="$route.path"`), ihre `watch`-Beobachter laufen also NICHT mit. Ein
// Store setzt sein Abo deshalb nur dann sicher auf die neue Firma um, wenn ein
// IMMER laufender Beobachter es tut — das ist allein der Auftrags-Watcher hier
// in `App.vue`. Je Eintrag steht in `beiFirmenwechsel` entweder die Handlung,
// die das Abo auf die vorige Firma beendet, oder der gemessene Beleg, warum
// keine noetig ist.
interface SitzungsAbo {
  /** Dateiname unter `src/stores/` ohne Endung — der Schluessel des Waechters. */
  store: string;
  /** Beendet JEDES Abo dieses Stores (Abmeldung, Praez. 1 (a)). */
  beiAbmeldung: () => void;
  /** Beendet das Abo auf die vorige Firma — oder der gemessene Beleg (Praez. 1 (b)). */
  beiFirmenwechsel: (() => void) | { gemessen: string };
}

const SITZUNGS_ABOS: readonly SitzungsAbo[] = [
  {
    store: 'companyStore',
    beiAbmeldung: () => { companyStore.unsubscribe(); companyStore.stopMemberSubscription(); },
    // Die Firmenliste ist nicht an eine Firma gebunden. Das MITGLIEDER-Abo ist
    // es (`companies/{id}/members`) — und es wird von keiner Ansicht beendet
    // (gemessen: kein Aufruf von `stopMemberSubscription` ausserhalb des
    // Stores; `CompanyDetailView` setzt es ohne Abraeumen).
    beiFirmenwechsel: () => companyStore.stopMemberSubscription(),
  },
  {
    store: 'printerStore',
    beiAbmeldung: () => printerStore.unsubscribe(),
    // Umgesetzt wird es nur von der gerade montierten Ansicht (Auftragsliste,
    // Uebersicht, Flotte, Drucker); `PrinterDetailView` und
    // `PrintserverDetailView` setzen es ohne Beobachter.
    beiFirmenwechsel: () => printerStore.unsubscribe(),
  },
  {
    store: 'printJobStore',
    beiAbmeldung: () => printJobStore.unsubscribe(),
    beiFirmenwechsel: {
      gemessen: 'App.vue, Auftrags-Watcher auf selectedCompanyId: subscribe(companyId) '
        + 'raeumt das vorige Abo vorher ab, ohne Firma unsubscribe() — laeuft immer.',
    },
  },
  {
    store: 'printserverStore',
    beiAbmeldung: () => printserverStore.unsubscribe(),
    // Sechs Ansichten setzen es ohne Abraeumen, umgesetzt nur von der gerade
    // montierten (Auftragsliste, Uebersicht, Flotte).
    beiFirmenwechsel: () => printserverStore.unsubscribe(),
  },
  {
    store: 'locationStore',
    beiAbmeldung: () => locationStore.unsubscribe(),
    // Keine Ansicht ausser der Flotte beobachtet die Firma; `LocationDetailView`
    // abonniert nur bei leerer Liste.
    beiFirmenwechsel: () => locationStore.unsubscribe(),
  },
  {
    store: 'userStore',
    beiAbmeldung: () => userStore.unsubscribe(),
    beiFirmenwechsel: {
      gemessen: 'nicht firmengebunden: `users` ohne Filter, allein fuer einen Admin.',
    },
  },
  {
    store: 'registerStore',
    beiAbmeldung: () => registerStore.unsubscribe(),
    // Firmengebunden, ohne jeden Beobachter (abonniert allein der Neuversuch
    // in `LiveVerbindungHinweis`).
    beiFirmenwechsel: () => registerStore.unsubscribe(),
  },
  {
    store: 'invitationStore',
    beiAbmeldung: () => invitationStore.unsubscribe(),
    beiFirmenwechsel: {
      gemessen: 'nicht firmengebunden: `invitations` ohne Filter, allein fuer einen Admin.',
    },
  },
  {
    store: 'apiTokenStore',
    beiAbmeldung: () => apiTokenStore.unsubscribe(),
    beiFirmenwechsel: {
      gemessen: 'ApiSettingsView: watch(companyId) setzt um, onUnmounted beendet — '
        + 'ausserhalb der Ansicht besteht kein Abo.',
    },
  },
];

/** Abmeldung: jedes Abo jedes Portal-Stores endet (Praez. 1 (a)). */
function beendeSitzungsAbos(): void {
  for (const eintrag of SITZUNGS_ABOS) eintrag.beiAbmeldung();
}

/** Firmenwechsel: kein Store haelt danach ein Abo auf die vorige Firma (Praez. 1 (b)). */
function beendeAbosDerVorigenFirma(): void {
  for (const eintrag of SITZUNGS_ABOS) {
    if (typeof eintrag.beiFirmenwechsel === 'function') eintrag.beiFirmenwechsel();
  }
}

watch(
  () => [authStore.isAuthenticated, authStore.isLoading] as const,
  ([authed, loading]) => {
    if (authed && !loading && laeuftAmPortal()) companyStore.subscribe();
    else                                        companyStore.unsubscribe();
    // Das Druckerabo wird hier NICHT gesetzt — es gehoert den Ansichten, die
    // die Liste brauchen (Flotte, Drucker, Druckerdetail und seit T-M17.6 die
    // Auftragsliste). BEENDET wird es hier, weil die Auftragsliste es bewusst
    // NICHT beim Verlassen abraeumt: ein Listener ohne angemeldeten Benutzer
    // lieferte sonst nach dem Abmelden „Missing or insufficient permissions".
    // Seit T-M48.6 gilt dasselbe fuer JEDES Portal-Abo — ueber die EINE Liste.
    if (!authed) beendeSitzungsAbos();
  },
  { immediate: true },
);

// Firmenwechsel (T-M48.6, REQ-OPS-053 Praez. 1 (b)): nur ein Wechsel VON einer
// Firma zaehlt — die erste Wahl (`null` → Kennung) hat keine vorige. Dieser
// Beobachter gehoert `App.vue`; dass er VOR dem Beobachter einer montierten
// Ansicht laeuft (Vue ordnet `pre`-Beobachter nach Komponenten-uid), ist
// plausibel, aber durch keinen Test belegt. Heute traegt es nicht: der einzige
// Wechselweg fuehrt ueber `/select-company` und baut die vorige Ansicht ab.
watch(
  () => companyStore.selectedCompanyId,
  (neu, vorige) => {
    if (vorige && neu !== vorige) beendeAbosDerVorigenFirma();
  },
);

// Subscribe to print jobs only when BOTH auth is ready AND a company is selected.
// Using watch on both values prevents the race condition where localStorage
// restores selectedCompanyId before Firebase Auth has resolved (→ Firestore
// returns "Missing or insufficient permissions" because request.auth is null).
//
// ⚠ **NACHGETRAGEN AM 2026-09-19 (T-M40.11, OP-200, REQ-OPS-058 Praez. 3).**
// *Berichtigt statt geloescht — REQ-OPS-036 Praez. 6.* **BIS DAHIN STAND HIER
// `if (authed && !loading && companyId)` OHNE ORTSBEZUG.** Im Feld gemessen
// (2026-09-08, `printerque-test`, Buendel `index-DWl8eq3o.js`): an einer
// Bildschirm-Adresse fielen DREI `abo.subscribed`-Zeilen — die zwei des Kanals
// plus dieses `printJobs`-Abo, samt dem 30-Sekunden-Takt der Kennzahlen, den
// `printJobStore.subscribe` mitbringt (`createRefreshTimer`,
// `JOB_COUNT_REFRESH_MS`, eine Seite zu `JOBS_PAGE_SIZE`). Ein Bildschirm, der
// tagelang laeuft, zog damit Abfragen, die er nie anzeigt.
//
// **DERSELBE `laeuftAmPortal()`-VORBEHALT WIE AM FIRMEN-WATCHER — DIESELBE EINE
// QUELLE** (REQ-OPS-038). Keine zweite Pfadwache, kein zweites Muster, keine
// Kopie der Adressliste. **Die Bildschirm-Ansichten sind davon unberuehrt:** sie
// fuehren ihr eigenes, engeres Abo (`useScreenChannel`) auf ihrer eigenen
// Anmelde-Instanz (REQ-SC-109). **`pf_company_id` bleibt stehen**
// (REQ-OPS-058 Praez. 2 (g1)) — sie zu entfernen waere die Ruecknahme von
// T-M34.10 und ist ausdruecklich nicht gemeint.
watch(
  () => [authStore.isAuthenticated, authStore.isLoading, companyStore.selectedCompanyId] as const,
  ([authed, loading, companyId]) => {
    if (authed && !loading && companyId && laeuftAmPortal()) {
      printJobStore.subscribe(companyId);
    } else {
      printJobStore.unsubscribe();
    }
  },
  { immediate: true },
);

// Bis T-M48.6 standen hier drei Einzelaufrufe (Auftrags-, Firmen-, Druckerabo);
// seither dieselbe EINE Liste wie beim Abmelden.
onUnmounted(beendeSitzungsAbos);
</script>

<style>
/* ═══════════════════════════════════════════════════════════════
   PAYFIX AG – Design System
   Light theme · Payfix Brand Colors
   ═══════════════════════════════════════════════════════════════ */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

:root {
  /* ── Payfix Brand Palette ─────────────────────────────────── */
  --pf-teal:        #4EC4BC;
  --pf-sky:         #7AAAC8;
  --pf-blue:        #3BBDE8;
  --pf-mid-blue:    #5492CC;
  --pf-blue-violet: #7878C8;
  --pf-violet:      #8070C0;
  --pf-purple:      #9060AA;
  --pf-dark:        #3C3C3C;

  /* ── Gradient (signature Payfix) ──────────────────────────── */
  --gradient-brand:    linear-gradient(135deg, #3BBDE8 0%, #7878C8 60%, #9060AA 100%);
  --gradient-brand-h:  linear-gradient(90deg, #3BBDE8 0%, #9060AA 100%);
  --gradient-subtle:   linear-gradient(135deg, rgba(59,189,232,0.08) 0%, rgba(144,96,170,0.08) 100%);

  /* ── Light Theme Surfaces ─────────────────────────────────── */
  --color-bg:        #EFF3F8;
  --color-surface:   #FFFFFF;
  --color-surface-2: #F5F8FB;
  --color-surface-3: #EBF0F7;
  --color-border:    rgba(0, 0, 0, 0.07);
  --color-shadow:    0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(59,189,232,0.06);
  --color-shadow-lg: 0 4px 6px rgba(0,0,0,0.04), 0 10px 40px rgba(59,189,232,0.10);

  /* ── Primary (Payfix Blue) ────────────────────────────────── */
  --color-primary:       #3BBDE8;
  --color-primary-dark:  #2BAAD4;
  --color-primary-dim:   rgba(59, 189, 232, 0.10);
  --color-primary-glow:  rgba(59, 189, 232, 0.20);

  /* ── Secondary (Payfix Purple) ───────────────────────────── */
  --color-secondary:     #9060AA;
  --color-secondary-dim: rgba(144, 96, 170, 0.10);

  /* ── Status Colors ────────────────────────────────────────── */
  --color-pending:   #F59E0B;
  --color-printing:  #3BBDE8;
  --color-completed: #10B981;
  --color-failed:    #EF4444;

  /* ── Text ─────────────────────────────────────────────────── */
  --text-primary:   #1A1F36;
  --text-secondary: #4A5568;
  --text-muted:     #94A3B8;
  --text-on-brand:  #FFFFFF;

  /* ── Layout ───────────────────────────────────────────────── */
  --sidebar-w:  250px;

  /* ── Seitenbreite (REQ-OPS-043, T-M18.5) ──────────────────────
     DIE EINE STELLE. Eine Verwaltungsseite waehlt hier eine der
     ZWEI benannten Breiten; ein eigener Zahlenwert im
     `<style scoped>` einer Ansicht ist unzulaessig und faellt in
     `test/seitenbreiten.test.ts` rot.

     Warum genau zwei: mehr Namen als Faelle waeren dieselbe
     Streuung mit anderen Mitteln. Es gibt zwei Faelle —
       • `--page-w-list` — Listen und Uebersichten (Auftragsliste,
         Dashboard, Drucker, Flotte, Benutzer, Standorte, …). Wert
         1500px: der WEITESTE Wert des Bestandes, aus der
         Auftragsliste, der Seite mit den meisten Spalten. Die
         Vereinheitlichung nimmt damit KEINER Listenseite Breite
         weg, sie gibt nur.
       • `--page-w-form` — Formulare und Detailseiten (Drucker-,
         Druckserver-, Benutzer-, Standort-, Mandantenformular,
         SMTP, Schankanlage). Wert 1000px: der weiteste Wert der
         Detailseiten des Bestandes (Mandanten-, Druckserver-
         Detail, SMTP). Ein Formular mit einer Feldspalte je Seite
         wird ab etwa dieser Breite nicht besser lesbar, sondern
         nur weiter auseinandergezogen.

     Der gemessene Anlass (2026-08-14, vor diesem Task): SIEBEN
     verschiedene Werte auf achtzehn Seiten — 860 / 960 / 1000 /
     1100 / 1200 / 1400 / 1500 —, jeder einzeln in einem
     `<style scoped>`. Ein Wert ist erklaerbar, sieben sind eine
     Drift.

     Die Bildschirmansichten (Kitchen TV, Abholscreen, Fastline)
     laufen im Vollbild und kennen keine Seitenbreite; die
     Anmelde- und Mandantenwahlkarten sind Bauteile, keine Seiten. */
  --page-w-list: 1500px;
  --page-w-form: 1000px;

  /* ── Hoehe des Testsystem-Kennzeichens (REQ-OPS-061, T-M32.7) ────────────
     DIE EINE STELLE. Der Streifen selbst (`components/TestumgebungBanner.vue`)
     und die Verschiebung des Rahmens weiter unten lesen denselben Wert; zwei
     Zahlen ergaeben einen Spalt oder eine Ueberdeckung. Im Produktionsbau
     rendert der Streifen nicht und die Variable bleibt ungenutzt. */
  --testkennzeichen-h: 24px;

  --radius-sm:  6px;
  --radius-md:  10px;
  --radius-lg:  16px;
  --radius-xl:  24px;

  /* ── Font ─────────────────────────────────────────────────── */
  --font-sans: 'Inter', system-ui, -apple-system, sans-serif;

  /* ── Transitions ──────────────────────────────────────────── */
  --t-fast: 150ms ease;
  --t-base: 250ms ease;
  --t-slow: 400ms cubic-bezier(0.16, 1, 0.3, 1);
}

/* ── Reset ──────────────────────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

html, body, #printerque-app { height: 100%; width: 100%; }

body {
  font-family:      var(--font-sans);
  background-color: var(--color-bg);
  color:            var(--text-primary);
  font-size:        14px;
  line-height:      1.6;
  -webkit-font-smoothing: antialiased;
}

/* ── App Layout ─────────────────────────────────────────────── */
#printerque-app { display: flex; min-height: 100vh; }

.app-main {
  flex:         1;
  margin-left:  var(--sidebar-w);
  min-height:   100vh;
  overflow-y:   auto;
}

/* ── Der Rahmen weicht dem Testsystem-Kennzeichen (T-M32.7, REQ-OPS-061) ─────
   Der Streifen ist `position: fixed` und laege sonst ueber dem Logo der
   Seitenleiste. Die Regeln greifen NUR, wenn die Wurzel die Klasse traegt —
   im Produktionsbau ist an der Darstellung damit keine Zeile anders.

   `.sidebar` ist in `AppSidebar.vue` `scoped` gesetzt (`.sidebar[data-v-…]`,
   zwei Klassen); der Selektor hier traegt eine ID und zwei Klassen und sticht
   ihn. Die Vollbild-Zustaende (Ladebild, Fehleransicht) rechnen mit `100vh` und
   bekommen die Hoehe des Streifens abgezogen, sonst ragten sie darunter
   hinaus. */
#printerque-app.hat-testkennzeichen { padding-top: var(--testkennzeichen-h); }

#printerque-app.hat-testkennzeichen .sidebar {
  top:    var(--testkennzeichen-h);
  height: calc(100vh - var(--testkennzeichen-h));
}

#printerque-app.hat-testkennzeichen .app-loading,
#printerque-app.hat-testkennzeichen .app-bootstrap-error {
  height: calc(100vh - var(--testkennzeichen-h));
}

#printerque-app.hat-testkennzeichen .app-main {
  min-height: calc(100vh - var(--testkennzeichen-h));
}

/* Wrapper for non-authenticated views (login) — fills full width */
.full-page-wrapper {
  flex:       1;
  width:      100%;
  display:    flex;
  flex-direction: column;
}

/* ── Loading Screen ─────────────────────────────────────────── */
.app-loading {
  display:         flex;
  flex-direction:  column;
  align-items:     center;
  justify-content: center;
  height:          100vh;
  width:           100vw;
  gap:             24px;
  background:      var(--color-bg);
}

.loading-logo {
  animation: loading-pop 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes loading-pop {
  from { opacity: 0; transform: scale(0.8); }
  to   { opacity: 1; transform: scale(1); }
}

.loading-bar {
  width:         200px;
  height:        3px;
  background:    var(--color-border);
  border-radius: 2px;
  overflow:      hidden;
}

.loading-bar-fill {
  height:     100%;
  background: var(--gradient-brand-h);
  border-radius: 2px;
  animation:  loading-slide 1.6s ease-in-out infinite;
}

@keyframes loading-slide {
  0%   { width: 0%;   margin-left: 0; }
  50%  { width: 60%;  margin-left: 20%; }
  100% { width: 0%;   margin-left: 100%; }
}

/* ── Fehleransicht des Anmelde-Starts (REQ-OPS-052 (d)) ─────── */
.app-bootstrap-error {
  display:         flex;
  flex-direction:  column;
  align-items:     center;
  justify-content: center;
  height:          100vh;
  width:           100vw;
  gap:             16px;
  padding:         24px;
  text-align:      center;
  background:      var(--color-bg);
}

.bootstrap-error-title {
  font-size:   20px;
  font-weight: 600;
  color:       var(--text-primary);
}

.bootstrap-error-hint {
  max-width: 480px;
  color:     var(--text-secondary);
}

.bootstrap-error-code {
  font-family:    ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size:      13px;
  color:          var(--text-muted);
  background:     var(--color-surface-2);
  border:         1px solid var(--color-border);
  border-radius:  var(--radius-sm);
  padding:        6px 10px;
}

.bootstrap-error-actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }

.bootstrap-btn {
  font-family:    var(--font-sans);
  font-size:      14px;
  font-weight:    500;
  padding:        10px 18px;
  border:         1px solid var(--color-border);
  border-radius:  var(--radius-md);
  background:     var(--color-surface);
  color:          var(--text-primary);
  cursor:         pointer;
  transition:     background var(--t-fast), box-shadow var(--t-fast);
}

.bootstrap-btn:hover { background: var(--color-surface-3); }

.bootstrap-btn-primary {
  background:   var(--gradient-brand-h);
  border-color: transparent;
  color:        var(--text-on-brand);
}

.bootstrap-btn-primary:hover { box-shadow: var(--color-shadow-lg); }

/* ── Page Transitions ───────────────────────────────────────── */
.page-enter-active,
.page-leave-active { transition: opacity var(--t-base), transform var(--t-base); }
.page-enter-from   { opacity: 0; transform: translateY(6px); }
.page-leave-to     { opacity: 0; transform: translateY(-6px); }

/* ── Scrollbar ──────────────────────────────────────────────── */
::-webkit-scrollbar        { width: 5px; }
::-webkit-scrollbar-track  { background: transparent; }
::-webkit-scrollbar-thumb  { background: var(--color-surface-3); border-radius: 3px; }
::-webkit-scrollbar-thumb:hover { background: #CBD5E0; }

/* ── Global Utilities ───────────────────────────────────────── */
.sr-only {
  position: absolute; width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0,0,0,0); border: 0;
}

/* ── Shared card style ──────────────────────────────────────── */
.pf-card {
  background:    var(--color-surface);
  border:        1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow:    var(--color-shadow);
}
</style>
